import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';

import { BackLink, Typography } from '../../components/atoms';
import { LoadingSpinner, Page } from '../../components/layouts';
import { NavList } from '../../components/molecules';
import { NavListVariantType } from '../../components/molecules/NavListItem/NavListItem';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService } from '../../core/services';
import { domUtils, storeListUtils, urlUtils } from '../../utils';
import { Track } from '../../weborder/analytics';
import { CollectionRestaurantSummary } from '../../weborder/Api';
import {
  DispatchProps,
  loadStatus,
  StateProps,
} from '../../weborder/component/CollectionList';

type Props = DispatchProps & StateProps;

const useStyles = makeStyles((theme: Theme) => ({
  disabled: {
    '& .fd-text': {
      color: theme.palette.grey[600],
    },
  },

  restaurantStatus: {
    '&.preOrder .fd-icon, &.preOrder span': {
      color: theme.palette.warning.main,
    },
  },

  restaurantTitle: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(0.25),

    '& .fd-icon': {
      marginRight: theme.spacing(1),
    },
  },
}));

export function StoreCollectionList(props: Props): JSX.Element {
  const {
    AppConfig: appConfig,
    collectionSummaries: collectionSummaryList,
    FindInitialRestaurant: findInitialRestaurant,
    LastRestaurantUrl: lastRestaurantUrl,
    translate,
  } = props;

  const {
    checkIsTableServiceOnly,
    isRestaurantClosedWithNoPreOrders,
    renderAvailabilityStatusIcon,
  } = storeListUtils;

  const classes = useStyles();
  const [loadSummaryListStatus, setLoadSummaryListStatus] = useState<
    loadStatus
  >('NOT_LOADED');

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    if (collectionSummaryList.status === 'success') {
      setLoadSummaryListStatus('LOADED');
    }
  }, [collectionSummaryList.status]);

  useEffect(() => {
    if (
      collectionSummaryList.status !== 'success' &&
      loadSummaryListStatus === 'NOT_LOADED'
    ) {
      findInitialRestaurant();
      setLoadSummaryListStatus('LOADING');
    }
  }, [collectionSummaryList.status, loadSummaryListStatus]);

  useEffect(() => {
    if (
      appConfig.status === 'success' &&
      collectionSummaryList.status === 'success' &&
      collectionSummaryList.data.length === 0
    ) {
      urlUtils.navigateToHash('/landing');
    }
  }, [appConfig.status, collectionSummaryList.status]);

  const getListItem = (collectionRestaurant: CollectionRestaurantSummary) => {
    if (Object.keys(collectionRestaurant).length === 0) {
      return null;
    }

    const {
      AcceptPreOrders: acceptPreOrders,
      HasConcessionStore: hasConcessionStore,
      IsOpen: isOpen,
      PhysicalRestaurantAddress: physicalRestaurantAddress,
      PhysicalRestaurantName: physicalRestaurantName,
    } = collectionRestaurant;

    const restaurantAddress = physicalRestaurantAddress.trim();
    const openingMessage = getOpeningTimeMessage(collectionRestaurant);
    const showSecondaryDetails = !!restaurantAddress;

    const itemVariant: NavListVariantType = isRestaurantClosedWithNoPreOrders(
      acceptPreOrders,
      isOpen,
    )
      ? 'noEndIcon'
      : 'default';

    const restaurantDetails = {
      elements: [
        renderRestaurantItem(
          collectionRestaurant,
          showSecondaryDetails,
          restaurantAddress,
          openingMessage,
        ),
      ],
      linkCallback: () =>
        trackSelectStore(hasConcessionStore, physicalRestaurantName),
      linkId: getStoreUrl(collectionRestaurant),
      linkPath: getStoreUrl(collectionRestaurant),
      variant: itemVariant,
    };

    return restaurantDetails;
  };

  const getOpeningTimeMessage = (
    collectionRestaurant: CollectionRestaurantSummary,
  ): JSX.Element => {
    const {
      IsOpen: isOpen,
      AcceptPreOrders: acceptPreOrders,
    } = collectionRestaurant;

    if (isOpen) {
      return null;
    }

    let preOrder = '';
    if (acceptPreOrders) {
      preOrder = translate('StoreCollectionList_PreorderNow');
    }

    const restaurantStatusStyles = clsx(
      classes.restaurantStatus,
      preOrder && 'preOrder',
      !preOrder && 'closed',
    );

    let statusPreOrderMessage = preOrder || '';
    statusPreOrderMessage = `${statusPreOrderMessage} `;

    return (
      <Typography
        className={restaurantStatusStyles}
        data-fd="opening-time-message"
        variant="body2-secondary"
      >
        <span>{statusPreOrderMessage}</span>
        {renderOpeningTimeMessage(collectionRestaurant)}
      </Typography>
    );
  };

  const getStoreUrl = (
    collectionRestaurant: CollectionRestaurantSummary,
  ): string => {
    const {
      VirtualRestaurantId: virtualRestaurantId,
      PhysicalRestaurantId: physicalRestaurantId,
    } = collectionRestaurant;

    return `/restaurant/${virtualRestaurantId}/collection/${physicalRestaurantId}`;
  };

  const sortByOpenStatusAndDistance = (a, b): number => {
    if (a.IsOpen > b.IsOpen) return -1;
    if (a.IsOpen < b.IsOpen) return 1;

    if (a.DistanceKm > b.DistanceKm) return 1;
    if (a.DistanceKm < b.DistanceKm) return -1;

    return 0;
  };

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Store Collection List',
    };
    analyticsService.track(eventData);
  };

  const trackSelectStore = (
    isConcessionStore: boolean,
    storeName: string,
  ): void => {
    let numberOfRestaurants = 0;
    if (collectionSummaryList.status === 'success') {
      numberOfRestaurants = collectionSummaryList.data.length;
    }

    const eventData = {
      amp_hasMenuZones: isConcessionStore,
      amp_label: 'Store Selected',
      amp_orderType: 'Collection',
      amp_storeSelected: storeName,
      amp_storesPresentedCount: numberOfRestaurants,
      eventLabel: storeName,
      label: 'Store Selected',
      section: 'Restaurants',
    };

    Track('Restaurants', 'Store Selected', eventData);
  };

  const renderBackLink = (): JSX.Element => {
    if (appConfig.status === 'success' && appConfig.data.DeliveryEnabled) {
      return (
        <BackLink
          data-fd="collection-back"
          label={translate('Back')}
          linkPath="/where"
          onClick={trackBackSelected}
        />
      );
    }

    if (lastRestaurantUrl !== '/') {
      return (
        <BackLink
          label={translate('Menu')}
          linkPath={lastRestaurantUrl}
          onClick={trackBackSelected}
        />
      );
    }

    return null;
  };

  const renderOpeningTimeMessage = (
    collectionRestaurant: CollectionRestaurantSummary,
  ): string => {
    const { OpenTimeMessage: openTimeMessage } = collectionRestaurant;

    if (!openTimeMessage) {
      return '';
    }

    return openTimeMessage;
  };

  const renderRestaurantItem = (
    collectionRestaurant: CollectionRestaurantSummary,
    showSecondaryDetails: boolean,
    restaurantAddress: string,
    openingTimeMessage: JSX.Element,
  ): JSX.Element => {
    const {
      AcceptPreOrders: acceptPreOrders,
      IsOpen: isOpen,
      PhysicalRestaurantId: physicalRestaurantId,
      PhysicalRestaurantName: physicalRestaurantName,
    } = collectionRestaurant;

    const restaurantStatusStyles = clsx(
      isRestaurantClosedWithNoPreOrders(acceptPreOrders, isOpen) &&
        classes.disabled,
    );

    return (
      <div
        className={restaurantStatusStyles}
        key={`store-${physicalRestaurantId}`}
      >
        <div className={classes.restaurantTitle}>
          {renderAvailabilityStatusIcon(acceptPreOrders, isOpen)}

          <Typography>
            <em>{physicalRestaurantName}</em>
          </Typography>
        </div>

        {showSecondaryDetails && (
          <>
            <Typography variant="body2-secondary">
              {restaurantAddress}
            </Typography>

            {!!openingTimeMessage && openingTimeMessage}
          </>
        )}
      </div>
    );
  };

  if (
    appConfig.status !== 'success' ||
    collectionSummaryList.status !== 'success'
  ) {
    return <LoadingSpinner />;
  }

  const sortedCollectionList = collectionSummaryList.data.sort((a, b) =>
    sortByOpenStatusAndDistance(a, b),
  );

  const collectionListItems = sortedCollectionList
    .map((collectionRestaurant) => getListItem(collectionRestaurant))
    .filter((collectionRestaurant) => collectionRestaurant !== null);

  const isTableServiceOnly = sortedCollectionList.every(
    (collectionRestaurant) => checkIsTableServiceOnly(collectionRestaurant),
  );

  const headerProps = !isTableServiceOnly
    ? {
        title: translate('StoreCollectionList_CollectAt'),
        subtitle: translate('StoreCollectionList_collectionListDescription'),
      }
    : {
        title: translate('StoreCollectionList_OrderTo'),
        subtitle: undefined,
      };

  return (
    <Page pageName="store-collection-list">
      <PageHeader showProfileMenu {...headerProps}>
        {renderBackLink()}
      </PageHeader>
      <article className="fd-content__main">
        <NavList items={collectionListItems} />
      </article>
    </Page>
  );
}
